import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import * as LucideIcons from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// PDF URL for the OsmAnd course
const CORSO_PDF_URL = "https://customer-assets-gfyr7b9c.emergentagent.net/job_61adbf5f-ea35-4c21-89a1-327367eeec28/artifacts/eq69x7ch_osmand-corso%20pdf.pdf";

// Helper to get icon component by name
const getIcon = (name, size = 24) => {
  if (!name) return null;
  const IconComponent = LucideIcons[name];
  if (!IconComponent) return null;
  return <IconComponent size={size} />;
};

// Parse icons string to array
const parseIcons = (iconsStr, size = 48) => {
  if (!iconsStr) return [];
  return iconsStr.split(',').map(name => getIcon(name.trim(), size)).filter(Boolean);
};

// Parse items JSON
const parseItems = (itemsJson) => {
  try {
    return JSON.parse(itemsJson || '[]');
  } catch {
    return [];
  }
};

const CorsoOsmand = () => {
  const { t, i18n } = useTranslation();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlay, setIsAutoPlay] = useState(false);
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('pdf'); // 'pdf' or 'interactive'

  // Fetch slides from API
  const fetchSlides = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/corso/slides`);
      setSlides(response.data || []);
    } catch (err) {
      console.error('Error fetching slides:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSlides();
  }, [fetchSlides]);

  // Auto-play functionality
  useEffect(() => {
    if (!isAutoPlay || slides.length === 0) return;
    const interval = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % slides.length);
    }, 8000);
    return () => clearInterval(interval);
  }, [isAutoPlay, slides.length]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight') nextSlide();
      if (e.key === 'ArrowLeft') prevSlide();
      if (e.key === 'Escape') setCurrentSlide(0);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [slides.length]);

  const nextSlide = () => {
    if (slides.length === 0) return;
    setCurrentSlide(prev => (prev + 1) % slides.length);
  };
  
  const prevSlide = () => {
    if (slides.length === 0) return;
    setCurrentSlide(prev => (prev - 1 + slides.length) % slides.length);
  };

  // Get background class
  const getBgClass = (slide) => {
    const from = slide.bg_color_from || 'white';
    const to = slide.bg_color_to || 'white';
    
    if (from === to) {
      return `bg-${from}`;
    }
    return `bg-gradient-to-br from-${from} to-${to}`;
  };

  // Get text color based on background
  const getTextColor = (slide) => {
    const darkBgs = ['gray-800', 'gray-900', 'blue-600', 'blue-800', 'blue-900', 'orange-500', 'orange-600'];
    if (darkBgs.includes(slide.bg_color_from) || darkBgs.includes(slide.bg_color_to)) {
      return 'text-white';
    }
    return 'text-gray-900';
  };

  const renderSlide = (slide) => {
    const baseClasses = "min-h-[70vh] flex flex-col justify-center p-8 md:p-12";
    const bgClass = getBgClass(slide);
    const textColor = getTextColor(slide);
    const isLight = textColor === 'text-gray-900';
    const items = parseItems(slide.items_json);
    
    switch(slide.slide_type) {
      case 'cover':
        return (
          <div className={`${baseClasses} text-center ${bgClass} text-white relative overflow-hidden`}>
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-10 left-10 w-40 h-40 border-2 border-white rounded-full"></div>
              <div className="absolute bottom-20 right-20 w-60 h-60 border-2 border-white rounded-full"></div>
            </div>
            <div className="relative z-10">
              <div className="flex justify-center gap-6 mb-8 opacity-80">
                {parseIcons(slide.icons, 48).map((icon, i) => <div key={i} className="text-orange-300">{icon}</div>)}
              </div>
              <h1 className="text-5xl md:text-7xl font-black mb-4 tracking-tight">{slide.title}</h1>
              <h2 className="text-2xl md:text-3xl font-light mb-6 text-blue-100">{slide.subtitle}</h2>
              {slide.badge && (
                <span className="inline-block bg-orange-500 text-white px-6 py-2 rounded-full font-semibold mb-8">{slide.badge}</span>
              )}
              <p className="text-lg md:text-xl max-w-3xl mx-auto text-blue-100">{slide.content}</p>
            </div>
          </div>
        );
      
      case 'standard':
        return (
          <div className={`${baseClasses} ${bgClass} ${textColor}`}>
            <h2 className="text-4xl md:text-5xl font-bold mb-8">{slide.title}</h2>
            <p className={`text-xl md:text-2xl mb-8 max-w-4xl ${isLight ? 'text-gray-700' : 'text-gray-100'}`}>{slide.content}</p>
            {slide.highlight && (
              <div className="bg-gradient-to-r from-blue-50 to-orange-50 border-l-4 border-orange-500 p-6 rounded-r-xl mb-8 max-w-4xl">
                <p className="text-lg text-gray-800 font-medium">{slide.highlight}</p>
              </div>
            )}
            {items.length > 0 && (
              <div className="flex flex-wrap gap-8 mt-6">
                {items.map((item, i) => (
                  <div key={i} className="flex flex-col items-center">
                    <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center text-blue-600 mb-2">
                      {getIcon(item.icon, 32)}
                    </div>
                    <span className="text-sm text-gray-600">{item.label || item.text}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case 'feature':
        return (
          <div className={`${baseClasses} ${bgClass} ${textColor}`}>
            <h2 className="text-4xl md:text-5xl font-bold mb-8">{slide.title}</h2>
            <p className={`text-xl mb-10 max-w-4xl ${isLight ? 'text-gray-700' : 'text-gray-100'}`}>{slide.content}</p>
            {items.length > 0 && (
              <div className="grid md:grid-cols-3 gap-6 mb-8 max-w-4xl">
                {items.map((f, i) => (
                  <div key={i} className="bg-white p-6 rounded-2xl shadow-lg flex items-start gap-4">
                    <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center text-orange-600 flex-shrink-0">
                      {getIcon(f.icon, 24)}
                    </div>
                    <span className="text-gray-800 font-medium">{f.text}</span>
                  </div>
                ))}
              </div>
            )}
            {slide.note && (
              <p className="text-gray-600 italic max-w-3xl">{slide.note}</p>
            )}
          </div>
        );

      case 'benefits':
        return (
          <div className={`${baseClasses} ${bgClass} text-white`}>
            <h2 className="text-4xl md:text-5xl font-bold mb-12">{slide.title}</h2>
            <div className="grid md:grid-cols-2 gap-6 max-w-4xl">
              {items.map((b, i) => (
                <div key={i} className="bg-white/10 backdrop-blur p-6 rounded-2xl">
                  <div className="flex items-center gap-4 mb-3">
                    <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                      {getIcon(b.icon, 28)}
                    </div>
                    <h3 className="text-xl font-bold">{b.title}</h3>
                  </div>
                  <p className="text-orange-100">{b.desc}</p>
                </div>
              ))}
            </div>
            {slide.footer && <p className="mt-10 text-orange-100 text-lg">{slide.footer}</p>}
          </div>
        );

      case 'steps':
        return (
          <div className={`${baseClasses} ${bgClass} ${textColor}`}>
            <h2 className="text-4xl md:text-5xl font-bold mb-12">{slide.title}</h2>
            <div className="space-y-6 max-w-3xl">
              {items.map((s, i) => (
                <div key={i} className="flex items-center gap-6 bg-gray-50 p-6 rounded-2xl">
                  <div className="w-14 h-14 bg-blue-600 text-white rounded-2xl flex items-center justify-center font-bold text-xl flex-shrink-0">
                    {s.num || i + 1}
                  </div>
                  <div className="flex items-center gap-4 flex-1">
                    <div className="text-blue-600">{getIcon(s.icon, 24)}</div>
                    <span className="text-lg text-gray-800">{s.text}</span>
                  </div>
                </div>
              ))}
            </div>
            {slide.highlight && (
              <div className="mt-8 bg-orange-50 border-l-4 border-orange-500 p-6 rounded-r-xl max-w-3xl">
                <p className="text-orange-800 font-medium">{slide.highlight}</p>
              </div>
            )}
          </div>
        );

      case 'list':
        return (
          <div className={`${baseClasses} ${bgClass} text-white`}>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">{slide.title}</h2>
            {slide.intro && <p className="text-xl text-blue-100 mb-10">{slide.intro}</p>}
            <div className="grid md:grid-cols-2 gap-4 max-w-4xl">
              {items.map((item, i) => (
                <div key={i} className="bg-white/10 backdrop-blur p-5 rounded-xl flex items-center gap-4">
                  <div className="w-12 h-12 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    {getIcon(item.icon, 24)}
                  </div>
                  <div>
                    <h4 className="font-bold">{item.name}</h4>
                    <p className="text-sm text-blue-200">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            {slide.footer && <p className="mt-10 text-blue-200">{slide.footer}</p>}
          </div>
        );

      case 'profiles':
        return (
          <div className={`${baseClasses} ${bgClass} ${textColor}`}>
            <h2 className="text-4xl md:text-5xl font-bold mb-8">{slide.title}</h2>
            <p className={`text-xl mb-10 max-w-4xl ${isLight ? 'text-gray-700' : 'text-gray-100'}`}>{slide.content}</p>
            <div className="flex flex-wrap gap-4 mb-8">
              {items.map((p, i) => (
                <div key={i} className={`px-8 py-6 rounded-2xl flex items-center gap-4 ${p.active ? 'bg-orange-500 text-white shadow-lg' : 'bg-gray-100 text-gray-700'}`}>
                  {getIcon(p.icon, 28)}
                  <span className="font-bold text-lg">{p.name}</span>
                </div>
              ))}
            </div>
            {slide.note && <p className="text-gray-600 italic">{slide.note}</p>}
          </div>
        );

      case 'config':
        return (
          <div className={`${baseClasses} ${bgClass} text-white`}>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">{slide.title}</h2>
            {slide.intro && <p className="text-xl text-gray-300 mb-10">{slide.intro}</p>}
            <div className="grid md:grid-cols-2 gap-4 max-w-3xl">
              {items.map((c, i) => (
                <div key={i} className="flex items-center gap-4 bg-white/10 p-4 rounded-xl">
                  <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center flex-shrink-0">
                    {getIcon(c.icon, 20)}
                  </div>
                  <span className="text-lg">{c.text}</span>
                  <LucideIcons.CheckCircle size={20} className="ml-auto text-green-400" />
                </div>
              ))}
            </div>
          </div>
        );

      case 'tips':
        return (
          <div className={`${baseClasses} ${bgClass} ${textColor}`}>
            <h2 className="text-4xl md:text-5xl font-bold mb-10">{slide.title}</h2>
            <div className="space-y-4 max-w-3xl">
              {items.map((t, i) => (
                <div key={i} className="flex items-center gap-4 bg-white p-5 rounded-xl shadow-md">
                  <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center text-orange-600 flex-shrink-0">
                    {getIcon(t.icon, 24)}
                  </div>
                  <span className="text-lg text-gray-800">{t.tip || t.text}</span>
                </div>
              ))}
            </div>
            {slide.footer && <p className="mt-8 text-gray-600">{slide.footer}</p>}
          </div>
        );

      case 'qa':
        return (
          <div className={`${baseClasses} ${bgClass} ${textColor}`}>
            <h2 className="text-4xl md:text-5xl font-bold mb-10">{slide.title}</h2>
            <div className="grid md:grid-cols-2 gap-4 max-w-4xl">
              {items.map((q, i) => (
                <div key={i} className="flex items-center gap-4 bg-white p-6 rounded-xl shadow-md">
                  <LucideIcons.HelpCircle className="text-purple-600 flex-shrink-0" size={28} />
                  <span className="text-lg text-gray-800">{typeof q === 'string' ? q : q.text}</span>
                </div>
              ))}
            </div>
            {slide.footer && <p className="mt-10 text-2xl font-medium text-purple-600">{slide.footer}</p>}
          </div>
        );

      case 'conclusion':
        return (
          <div className={`${baseClasses} text-center ${bgClass} text-white`}>
            <div className="flex justify-center gap-8 mb-8 opacity-80">
              {parseIcons(slide.icons, 40).map((icon, i) => <div key={i} className="text-orange-300">{icon}</div>)}
            </div>
            <h2 className="text-5xl md:text-6xl font-bold mb-8">{slide.title}</h2>
            <p className="text-2xl text-blue-100 mb-8 max-w-3xl mx-auto">{slide.content}</p>
            {slide.cta && <p className="text-xl text-orange-200 mb-10 max-w-3xl mx-auto">{slide.cta}</p>}
            <Link 
              to="/events" 
              className="inline-flex items-center bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-full font-bold text-lg transition-all"
            >
              Scopri i nostri eventi
              <LucideIcons.ArrowRight className="ml-2" size={24} />
            </Link>
          </div>
        );

      default:
        return (
          <div className={`${baseClasses} ${bgClass} ${textColor}`}>
            <h2 className="text-4xl md:text-5xl font-bold mb-8">{slide.title}</h2>
            <p className="text-xl">{slide.content}</p>
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center text-white">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p>Caricamento corso...</p>
        </div>
      </div>
    );
  }

  // PDF Viewer Component
  const PDFViewer = () => (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800">
      {/* Header */}
      <div className="bg-gray-900 text-white py-4 px-6 flex items-center justify-between sticky top-0 z-50 shadow-lg">
        <Link to="/" className="flex items-center gap-3 text-white hover:text-orange-400 transition-colors">
          <LucideIcons.Home size={20} />
          <span className="hidden md:inline">Offroad Italia</span>
        </Link>
        <div className="flex items-center gap-3">
          {/* View Mode Toggle */}
          {slides.length > 0 && (
            <button
              onClick={() => setViewMode('interactive')}
              className="hidden md:flex items-center gap-2 bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg text-sm transition-all"
            >
              <LucideIcons.Presentation size={16} />
              {i18n.language === 'it' ? 'Modalità Interattiva' : 'Interactive Mode'}
            </button>
          )}
          {/* CTA Button - Sign Up */}
          <a
            href="https://osmand-course-signup.emergent.host/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 bg-[#D95D39] hover:bg-[#c44d2d] text-white font-bold px-5 py-2 rounded-full uppercase tracking-wider text-sm transition-all hover:scale-105"
          >
            <span className="hidden md:inline">{i18n.language === 'it' ? 'Iscriviti' : 'Sign Up'}</span>
            <LucideIcons.ArrowRight size={16} />
          </a>
        </div>
      </div>

      {/* Course Info Banner */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-8 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <div className="flex justify-center gap-4 mb-4">
            <LucideIcons.Compass size={32} className="text-orange-300" />
            <LucideIcons.Map size={32} className="text-orange-300" />
            <LucideIcons.Navigation size={32} className="text-orange-300" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-2">
            {i18n.language === 'it' ? 'Corso OsmAnd' : i18n.language === 'en' ? 'OsmAnd Course' : i18n.language === 'de' ? 'OsmAnd-Kurs' : i18n.language === 'fr' ? 'Cours OsmAnd' : 'Curso OsmAnd'}
          </h1>
          <p className="text-blue-100 text-lg">
            {i18n.language === 'it' ? 'Navigazione Professionale per Enduro & Adventure Riding' : i18n.language === 'en' ? 'Professional Navigation for Enduro & Adventure Riding' : i18n.language === 'de' ? 'Professionelle Navigation für Enduro & Adventure Riding' : i18n.language === 'fr' ? 'Navigation Professionnelle pour Enduro & Adventure Riding' : 'Navegación Profesional para Enduro & Adventure Riding'}
          </p>
          <p className="text-orange-200 mt-2 text-sm">
            {i18n.language === 'it' ? '14 Capitoli • 28 Slide • Guida Completa' : '14 Chapters • 28 Slides • Complete Guide'}
          </p>
        </div>
      </div>

      {/* PDF Embed - View Only (no download toolbar) */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Using Google Docs Viewer to prevent easy download */}
          <iframe
            src={`https://docs.google.com/viewer?url=${encodeURIComponent(CORSO_PDF_URL)}&embedded=true`}
            title="Corso OsmAnd - Offroad Italia"
            className="w-full"
            style={{ height: '80vh', minHeight: '600px' }}
            loading="lazy"
            sandbox="allow-scripts allow-same-origin"
          />
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gray-900 py-12 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            {i18n.language === 'it' ? 'Vuoi imparare di più?' : i18n.language === 'en' ? 'Want to learn more?' : i18n.language === 'de' ? 'Möchten Sie mehr erfahren?' : i18n.language === 'fr' ? 'Voulez-vous en savoir plus?' : '¿Quieres aprender más?'}
          </h2>
          <p className="text-gray-400 mb-8">
            {i18n.language === 'it' ? 'Iscriviti al nostro corso completo con supporto tecnico e community dedicata!' : i18n.language === 'en' ? 'Sign up for our complete course with technical support and dedicated community!' : i18n.language === 'de' ? 'Melden Sie sich für unseren kompletten Kurs mit technischem Support und einer engagierten Community an!' : i18n.language === 'fr' ? 'Inscrivez-vous à notre cours complet avec support technique et communauté dédiée!' : '¡Inscríbete en nuestro curso completo con soporte técnico y comunidad dedicada!'}
          </p>
          <a
            href="https://osmand-course-signup.emergent.host/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#D95D39] hover:bg-[#c44d2d] text-white font-bold px-8 py-4 rounded-full uppercase tracking-widest text-lg transition-all hover:scale-105 shadow-lg hover:shadow-xl"
          >
            {i18n.language === 'it' ? 'Iscriviti al Corso OsmAnd' : i18n.language === 'en' ? 'Sign Up for OsmAnd Course' : i18n.language === 'de' ? 'Zum OsmAnd-Kurs anmelden' : i18n.language === 'fr' ? 'Inscrivez-vous au cours OsmAnd' : 'Inscríbete al Curso OsmAnd'}
            <LucideIcons.ArrowRight size={20} />
          </a>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-gray-900 border-t border-gray-800 text-gray-400 text-center py-6 text-sm">
        <p>© Offroad Italia — {t('nav.corsoOsmand')}</p>
      </div>
    </div>
  );

  // Show PDF view by default, or if no interactive slides available
  if (viewMode === 'pdf' || slides.length === 0) {
    return <PDFViewer />;
  }

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <div className="bg-gray-900 text-white py-4 px-6 flex items-center justify-between sticky top-0 z-50">
        <Link to="/" className="flex items-center gap-3 text-white hover:text-orange-400 transition-colors">
          <LucideIcons.Home size={20} />
          <span className="hidden md:inline">Offroad Italia</span>
        </Link>
        <div className="flex items-center gap-4">
          {/* Back to PDF View */}
          <button
            onClick={() => setViewMode('pdf')}
            className="hidden md:flex items-center gap-2 bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-lg text-sm transition-all"
          >
            <LucideIcons.FileText size={16} />
            {i18n.language === 'it' ? 'Visualizza PDF' : 'View PDF'}
          </button>
          <span className="text-sm text-gray-400 hidden sm:inline">
            Slide {currentSlide + 1} / {slides.length}
          </span>
          <button
            onClick={() => setIsAutoPlay(!isAutoPlay)}
            className={`p-2 rounded-lg transition-colors ${isAutoPlay ? 'bg-orange-500' : 'bg-gray-700 hover:bg-gray-600'}`}
            title={isAutoPlay ? 'Pausa' : 'Auto-play'}
          >
            {isAutoPlay ? <LucideIcons.Pause size={20} /> : <LucideIcons.Play size={20} />}
          </button>
          {/* CTA Button - Sign Up */}
          <a
            href="https://osmand-course-signup.emergent.host/"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden md:inline-flex items-center gap-2 bg-[#D95D39] hover:bg-[#c44d2d] text-white font-bold px-5 py-2 rounded-full uppercase tracking-wider text-sm transition-all hover:scale-105"
          >
            Iscriviti al Corso
            <LucideIcons.ArrowRight size={16} />
          </a>
        </div>
      </div>

      {/* Slide Content */}
      <div className="relative">
        {renderSlide(slides[currentSlide])}
        
        {/* Navigation Arrows */}
        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/30 hover:bg-black/50 text-white rounded-full flex items-center justify-center transition-all"
          aria-label="Slide precedente"
        >
          <LucideIcons.ChevronLeft size={28} />
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/30 hover:bg-black/50 text-white rounded-full flex items-center justify-center transition-all"
          aria-label="Slide successiva"
        >
          <LucideIcons.ChevronRight size={28} />
        </button>
      </div>

      {/* Progress Bar */}
      <div className="bg-gray-800 px-6 py-4">
        <div className="flex gap-2 max-w-4xl mx-auto">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentSlide(i)}
              className={`h-2 rounded-full transition-all flex-1 ${i === currentSlide ? 'bg-orange-500' : 'bg-gray-600 hover:bg-gray-500'}`}
              aria-label={`Vai alla slide ${i + 1}`}
            />
          ))}
        </div>
      </div>

      {/* Footer with CTA */}
      <div className="bg-gray-900 text-center py-8">
        {/* Main CTA Button */}
        <a
          href="https://osmand-course-signup.emergent.host/"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 bg-[#D95D39] hover:bg-[#c44d2d] text-white font-bold px-8 py-4 rounded-full uppercase tracking-widest text-lg transition-all hover:scale-105 shadow-lg hover:shadow-xl mb-6"
        >
          {i18n.language === 'it' ? 'Iscriviti al Corso OsmAnd' : i18n.language === 'en' ? 'Sign Up for OsmAnd Course' : i18n.language === 'de' ? 'Zum OsmAnd-Kurs anmelden' : i18n.language === 'fr' ? 'Inscrivez-vous au cours OsmAnd' : 'Inscríbete al Curso OsmAnd'}
          <LucideIcons.ArrowRight size={20} />
        </a>
        
        <p className="text-gray-400 text-sm mt-4">{i18n.language === 'it' ? 'Usa le frecce ← → della tastiera per navigare' : i18n.language === 'en' ? 'Use the ← → arrow keys to navigate' : i18n.language === 'de' ? 'Verwenden Sie die Pfeiltasten ← → zum Navigieren' : i18n.language === 'fr' ? 'Utilisez les touches fléchées ← → pour naviguer' : 'Use las teclas de flecha ← → para navegar'}</p>
        <p className="text-gray-400 text-sm mt-2">© Offroad Italia — {t('nav.corsoOsmand')}</p>
      </div>
    </div>
  );
};

export default CorsoOsmand;
